import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map, flatMap, catchError } from 'rxjs/operators';
import { plainToClass } from 'class-transformer';
import { BookmarkModel } from './models/bookmark.model';
import { Observable, throwError, of } from 'rxjs';
import { StoryModel } from '../story/models/story.model';
import { SerializeModuleService } from '../edit/serialize-module.service';
import { TrimConfiguration } from 'src/app/edit/models/trim-config.model';
import { ExploreService } from '../explore/explore.service';
import { StoryType } from '../story/models/story-type.enum';
import * as _ from 'lodash';
import { Store } from '@ngxs/store';
import { StoryState } from '../story/state/story.state';
import { StoryChaptersState } from '../story/state/chapters/story.chapters.state';
import * as i0 from "@angular/core";
import * as i1 from "@ngxs/store";
import * as i2 from "@angular/common/http";
import * as i3 from "../edit/serialize-module.service";
import * as i4 from "../explore/explore.service";
var bookmarkServiceURL = '/ws/user/bookmark/v1';
var thumbnailServiceURL = '/ws/thumbs/bookmarks';
var favoritesServiceURL = '/ws/user/favorite';
var formHeaders = {
    headers: new HttpHeaders({
        'Content-Type': 'application/json; charset=utf-8'
    })
};
export var BookmarkCategory;
(function (BookmarkCategory) {
    BookmarkCategory["Private"] = "private";
    BookmarkCategory["Group"] = "enterprise";
    BookmarkCategory["Public"] = "public";
})(BookmarkCategory || (BookmarkCategory = {}));
var BookmarksService = /** @class */ (function () {
    function BookmarksService(store, http, serializeModuleService, exploreService) {
        this.store = store;
        this.http = http;
        this.serializeModuleService = serializeModuleService;
        this.exploreService = exploreService;
    }
    /**
     * Gets the bookmark data by the specified id
     * @param id The bookmark id
     */
    BookmarksService.prototype.getBookmarkData = function (id) {
        return this.http.get("/search/bookmarks/data?be=" + id)
            .pipe(map(function (result) {
            result = result.bookmark;
            result.moduleId = result.customData ? result.customData.moduleId : result.modules.activeModules[0];
            return plainToClass(StoryModel, result);
        }));
    };
    /**
     * Gets the bookmark model data by specified id
     * @param id The bookmark id
     */
    BookmarksService.prototype.getBookmarkModel = function (id) {
        var _this = this;
        return this.http.get("/ws/search/bookmarks?eid=" + id)
            .pipe(map(function (result) { return plainToClass(BookmarkModel, result.bookmark_info); }), map(function (bookmark) { return _this.normalizeBookmarkModel(bookmark); }));
    };
    /** Gets all user generated bookmarks by specified category
     * @param category Category of bookmarks
     * @param query Optional search query
     */
    BookmarksService.prototype.getUserBookmarks = function (category, query) {
        var _this = this;
        if (query === void 0) { query = ''; }
        return this.http.get("/ws/search/bookmarks/" + category + "?q=" + query)
            .pipe(map(function (results) {
            return results
                .map(function (result) { return plainToClass(BookmarkModel, result); })
                .map(function (bookmark) { return _this.normalizeBookmarkModel(bookmark); });
        }));
    };
    /**
     * Gets a list of user mapped custom conditions.
     */
    BookmarksService.prototype.getUserConditions = function (category, id) {
        var _this = this;
        var moduleAccessorLevel = '';
        switch (category) {
            case BookmarkCategory.Private:
                moduleAccessorLevel = 'user';
                break;
            case BookmarkCategory.Group:
                moduleAccessorLevel = 'group';
                break;
            default:
                break;
        }
        return this.http.get("/ws/search/conditions/private")
            .pipe(
        // restrict by type + id
        map(function (results) {
            return results
                .filter(function (result) {
                var matchedByType = result.module_accessor_level === moduleAccessorLevel;
                var matchedById = !id || result.module_accessor_id === id;
                return matchedByType && matchedById;
            });
        }), 
        // to bookmarks
        map(function (results) { return plainToClass(BookmarkModel, results); }), 
        // normalize
        map(function (bookmarks) {
            return bookmarks
                .map(function (bookmark) { return _this.normalizeBookmarkModel(bookmark); });
        }));
    };
    /**
     * Gets a list of private bookmarks only accessible to user
     * @param query Optional search query
     */
    BookmarksService.prototype.getPrivateBookmarks = function (query) {
        if (query === void 0) { query = ''; }
        return this.getUserBookmarks(BookmarkCategory.Private, query)
            .pipe(map(function (bookmarks) {
            return bookmarks.filter(function (bookmark) { return bookmark.is_owner; });
        }));
    };
    /**
     * Gets a list of user favorited content, by favorite id.
     */
    BookmarksService.prototype.getFavorites = function () {
        var _this = this;
        return this.http.get(favoritesServiceURL)
            .pipe(map(function (results) {
            return Object.values(results)
                .map(function (result) { return plainToClass(BookmarkModel, result); })
                .map(function (bookmark) { return _this.normalizeBookmarkModel(bookmark); });
        }));
    };
    /**
     * Deletes the specified content in the user's favorites.
     */
    BookmarksService.prototype.deleteFavorite = function (contentId, contentType) {
        return this.http.delete(favoritesServiceURL + "?modelId=" + contentId + "&category=" + contentType);
    };
    /* Gets a list of user custom conditions.
    */
    BookmarksService.prototype.getPrivateConditions = function () {
        return this.getUserConditions(BookmarkCategory.Private);
    };
    /**
     * Gets a list of bookmarks for the specified organziation
     * @param organizationId The organization id for the bookmarks
     * @param query: Optional search query
     */
    BookmarksService.prototype.getOrganizationBookmarks = function (organizationId, query) {
        if (query === void 0) { query = ''; }
        return this.getUserBookmarks(BookmarkCategory.Private, query)
            .pipe(map(function (bookmarks) {
            // filter enterprise bookmarks
            return bookmarks
                .filter(function (bookmark) { return bookmark.tags.includes('enterprise'); })
                // restrict to group accessibility
                .filter(function (bookmark) { return bookmark.group_access.some(function (access) { return access.group_id === organizationId; }); });
        }));
    };
    /**
   * Gets a list of group custom conditions.
   */
    BookmarksService.prototype.getOrganizationConditions = function (organizationId) {
        return this.getUserConditions(BookmarkCategory.Group, organizationId);
    };
    // create a new bookmark object
    BookmarksService.prototype.createBookmarkStory = function (inplace) {
        if (inplace === void 0) { inplace = false; }
        return this.serializeModuleService.serialize(inplace, {}, new TrimConfiguration(), false, this.store.selectSnapshot(StoryState.getStory), this.store.selectSnapshot(StoryChaptersState.getStoryChapters));
    };
    /**
     * Gets a list of public bookmarks
     * @param query: Optional search query
     */
    BookmarksService.prototype.getPublicBookmarks = function (query) {
        if (query === void 0) { query = ''; }
        return this.getUserBookmarks(BookmarkCategory.Public, query);
    };
    BookmarksService.prototype.saveBookmark = function (entry) {
        var _this = this;
        // normalize to required service params
        var normalizedEntry = this.getNormalizedBookmarkEntry(entry);
        var response$;
        if (entry.id) {
            // update bookmark
            response$ = this.http.post(bookmarkServiceURL + "/update", normalizedEntry, formHeaders);
        }
        else {
            // save new bookmark
            response$ = this.http.post(bookmarkServiceURL + "/save", normalizedEntry, formHeaders);
        }
        return response$.pipe(
        // normalize to bookmark model
        flatMap(function (response) { return _this.getBookmarkModel(response.id); }));
    };
    BookmarksService.prototype.deleteBookmark = function (id) {
        return this.http.delete(bookmarkServiceURL + "/delete?eid=" + id)
            .pipe(catchError(function (response) {
            var error = response.error.error;
            var message = error ? error.message : '';
            return throwError(message);
        }));
    };
    BookmarksService.prototype.copyBookmark = function (id, type) {
        // Fix module id truncation
        if (type === StoryType.Module && !_.endsWith(id, '.json')) {
            id += '.json';
        }
        return this.http.post(bookmarkServiceURL + "/copy", { source_content_type: type, source_content_id: id })
            .pipe(map(function (response) { return response.id; }));
    };
    BookmarksService.prototype.atMaxFreeBookmarkCount = function () {
        return this.http.get("/ws/user/content/limit/check")
            .pipe(map(function () { return false; }), catchError(function (response) { return of(response.status === 403); }));
    };
    // save thumbnail data for each chapter in a bookmark
    BookmarksService.prototype.saveBookmarkThumbnails = function (id, bookmarkEntry, thumbnails, chapterIdMap) {
        var _this = this;
        // chapter thumbnails
        var thumbnailRequests$ = bookmarkEntry.index.chapters.reduce(function (requests, chapter) {
            var newChapterId = chapter.id;
            var oldChapterId = chapterIdMap[newChapterId];
            var currentThumbnail = thumbnails.get(oldChapterId);
            if (currentThumbnail && currentThumbnail.snapshot) {
                requests.push(_this.postThumbnail(id, chapter.id, currentThumbnail.snapshot));
            }
            else if (chapter.id !== chapterIdMap[chapter.id] || currentThumbnail.chapterId !== chapter.id) {
                var splitIdx = currentThumbnail.urls.large.match(/(bookmarks\/)|(modules\/)/).index;
                var srcURL = currentThumbnail.urls.large.slice(splitIdx);
                requests.push(_this.copyThumbnail(id, chapter.id, srcURL));
            }
            return requests;
        }, []);
        // bookmark gallery thumbnail
        var firstChapterThumbnail = thumbnails.get(chapterIdMap[bookmarkEntry.index.chapters[0].id]);
        if (firstChapterThumbnail.snapshot) {
            // create new
            thumbnailRequests$.push(this.postThumbnail(id, null, firstChapterThumbnail.snapshot));
        }
        else {
            // copy existing
            thumbnailRequests$.push(this.copyThumbnail(id, null, firstChapterThumbnail.urls.large));
        }
        return thumbnailRequests$;
    };
    // delete thumbnails for deleted chapters
    BookmarksService.prototype.deleteBookmarkThumbnails = function (id, bookmarkEntry, thumbnails, chapterIdMap) {
        var _this = this;
        /*
        delete existing (server stored) thumbnail urls on deleted chapters
        */
        var chapterIds = bookmarkEntry.index.chapters.map(function (chapter) { return chapterIdMap[chapter.id] || chapter.id; });
        var deleteRequests$ = Array.from(thumbnails.values()).filter(function (thumbnail) { return chapterIds.indexOf(thumbnail.chapterId) < 0; })
            .map(function (thumbnail) {
            return _this.deleteThumbnail(id, thumbnail.chapterId);
        });
        return deleteRequests$;
    };
    /**
     * Filters the specified list by tags
     * @param bookmarks The list of bookmarks
     * @param tags The list of tags
     * @param inclusive True to match all tags, else single match
     */
    BookmarksService.prototype.filterByTags = function (bookmarks, tags, inclusive) {
        if (inclusive === void 0) { inclusive = false; }
        return bookmarks.filter(function (bookmark) {
            if (inclusive) {
                return tags.every(function (tag) { return bookmark.tags.includes(tag); });
            }
            else {
                return tags.some(function (tag) { return bookmark.tags.includes(tag); });
            }
        });
    };
    BookmarksService.prototype.canEdit = function (bookmark) {
        if (bookmark.is_owner) {
            return true;
        }
        if (bookmark.group_access && bookmark.group_access.length) {
            return bookmark.group_access[0].member_edit;
        }
        return false;
    };
    BookmarksService.prototype.normalizeBookmarkModel = function (bookmark) {
        var id = bookmark.id, type = bookmark.type;
        // normalize id
        if (type === 'module') {
            bookmark.id = this.exploreService.toAppContentId(id);
        }
        // normalize tag properties
        bookmark.tour = bookmark.tags.includes('tour');
        bookmark.animated = bookmark.tags.includes('animated');
        return bookmark;
    };
    BookmarksService.prototype.getNormalizedBookmarkEntry = function (entry) {
        var _this = this;
        var id = entry.id, title = entry.title, description = entry.description, index = entry.index, public_access = entry.public_access, add_group_access = entry.add_group_access, remove_group_access = entry.remove_group_access, isOwner = entry.isOwner;
        // optional user (owner) fields
        var group_access;
        var publicAccess;
        var flags;
        if (isOwner) {
            // set additional group access    
            group_access = add_group_access ? this.getGroupAccess(add_group_access) : [];
            // revoke group access
            if (remove_group_access) {
                remove_group_access.forEach(function (access) {
                    var removedAccess = _this.getNormalizeGroupAccess({
                        group_id: access.group_id,
                        remove: true
                    });
                    group_access.push(removedAccess);
                });
            }
            // set public access
            publicAccess = this.getNormalizedBool(public_access);
            // custom flags
            if (index) {
                // init flags
                flags = {};
                // flag: user generated tours
                if (index.chapters) {
                    var isTour = index.chapters.length > 1;
                    flags['user_generated_tour'] = this.getNormalizedBool(isTour);
                }
                // flag: animated content
                var isAnimated = index.tags.includes('animated');
                flags['animated'] = this.getNormalizedBool(isAnimated);
            }
        }
        // normalized service entry
        return {
            eid: id,
            name: title,
            description: description,
            // serialized story
            index: index,
            // permissions: set null for no updates
            group_access: group_access,
            public_access: publicAccess,
            // custom bookmark flags
            flags: flags
        };
    };
    BookmarksService.prototype.getGroupAccess = function (groupAccess) {
        var _this = this;
        return groupAccess.map(function (access) { return _this.getNormalizeGroupAccess(access); });
    };
    BookmarksService.prototype.getNormalizeGroupAccess = function (access) {
        var group_id = access.group_id, group_name = access.group_name, member_edit = access.member_edit, visible = access.visible, remove = access.remove;
        var normalized = {
            group_id: group_id,
            group_name: group_name,
            member_edit: this.getNormalizedBool(member_edit),
            visibility: this.getNormalizedBool(visible),
            remove: this.getNormalizedBool(remove)
        };
        return normalized;
    };
    BookmarksService.prototype.getNormalizedBool = function (value) {
        return value === true ? '1' : (value === false ? '0' : '');
    };
    // request to server to save a thumbnail
    BookmarksService.prototype.postThumbnail = function (storyId, chapterId, thumbnailURL) {
        var _this = this;
        var image = new Image();
        image.src = thumbnailURL;
        return new Observable(function (observer) {
            image.onload = function () {
                // transfer image to canvas
                var canvas = document.createElement('canvas');
                var context = canvas.getContext('2d');
                canvas.width = image.naturalWidth;
                canvas.height = image.naturalHeight;
                context.drawImage(image, 0, 0);
                // convert to data url
                var dataURL = canvas.toDataURL('image/jpeg', 1.0);
                // post request for thumbnail
                var _thumbnailURL = '';
                if (chapterId) {
                    // chapter thumbnail
                    _thumbnailURL = thumbnailServiceURL + "/" + storyId + "/large/chapters/" + chapterId + ".jpg";
                }
                else {
                    // story thumbnail
                    _thumbnailURL = thumbnailServiceURL + "/" + storyId + "/large/index.jpg";
                }
                var headers = new HttpHeaders().set('Content-Type', 'application/json');
                _this.http.post(_thumbnailURL, { image: dataURL }, { headers: headers, responseType: 'text' }).subscribe(function (r) {
                    observer.next();
                    observer.complete();
                });
            };
        });
    };
    // request to server to delete a thumbnail
    BookmarksService.prototype.deleteThumbnail = function (storyId, chapterId) {
        return new Observable(function (observer) {
            var request = new Request(thumbnailServiceURL + "/" + storyId + "/large/chapters/" + chapterId + ".jpg", { method: 'DELETE' });
            fetch(request).then(function (response) {
                observer.complete();
            });
        });
    };
    // request to server to copy a thumbnail from one location to another
    BookmarksService.prototype.copyThumbnail = function (storyId, chapterId, src) {
        var _this = this;
        var dst = '';
        var srcURL = src.replace('/thumbs/', ''); // 
        if (chapterId) {
            // copy to chapter location
            dst = "bookmarks/" + storyId + "/large/chapters/" + chapterId + ".jpg";
        }
        else {
            // copy to index location
            dst = "bookmarks/" + storyId + "/large/index.jpg";
        }
        var header = new HttpHeaders().set('Content-Type', 'application/json');
        return new Observable(function (observer) {
            _this.http.post('/ws/thumbs/copy', { src: srcURL, dst: dst }, { headers: header, responseType: 'text' }).subscribe(function (r) {
                observer.next();
                observer.complete();
            });
        });
    };
    BookmarksService.ngInjectableDef = i0.defineInjectable({ factory: function BookmarksService_Factory() { return new BookmarksService(i0.inject(i1.Store), i0.inject(i2.HttpClient), i0.inject(i3.SerializeModuleService), i0.inject(i4.ExploreService)); }, token: BookmarksService, providedIn: "root" });
    return BookmarksService;
}());
export { BookmarksService };
